import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Paper,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import { ClothingItem, ClothingType } from "../types/wardrobe";
import { api } from "../services/api";
import { isMonochromaticOutfit } from "../utils/colorUtils";
import {
  hybridColorService,
  HybridColorCompatibilityResult,
} from "../services/hybridColorService";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useResponsive } from "../hooks/useResponsive";

interface OutfitBuilderProps {
  items: ClothingItem[];
  onItemUpdate: (updatedItems: ClothingItem[]) => void;
}

interface State {
  selectedItem: ClothingItem | null;
  compatibleItems: ClothingItem[];
  selectedCategory: ClothingType | "all";
  selectedOutfit: ClothingItem[];
  selectedIds: Set<string>;
}

// Season definitions with transition months
const SEASON_MONTHS: Record<string, number[]> = {
  winter: [12, 1, 2], // December, January, February
  spring: [3, 4, 5, 6], // March, April, May, early June
  summer: [7, 8, 9], // July, August, early September
  autumn: [9, 10, 11], // late September, October, November
};

// Define transition months (last month of each season)
const TRANSITION_MONTHS = {
  spring: 6, // June
  summer: 9, // September
  autumn: 12, // December
  winter: 3, // March
};

interface SeasonWeights {
  primary: { season: string; weight: number };
  secondary?: { season: string; weight: number };
}

const getSeasonWeights = (): SeasonWeights => {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-12
  const dayOfMonth = now.getDate(); // 1-31

  // Find current season
  let currentSeason = "spring";
  for (const [season, months] of Object.entries(SEASON_MONTHS)) {
    if (months.includes(month)) {
      currentSeason = season;
      break;
    }
  }

  // If we're in a transition month
  if (Object.values(TRANSITION_MONTHS).includes(month)) {
    const nextSeason = (() => {
      const seasons = ["winter", "spring", "summer", "autumn"];
      const currentIndex = seasons.indexOf(currentSeason);
      return seasons[(currentIndex + 1) % seasons.length];
    })();

    // Calculate weights for gradual transition throughout the month
    // Start of month: 70% current season, 30% next season
    // End of month: 40% current season, 60% next season
    const daysInMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).getDate();
    const progress = (dayOfMonth - 1) / (daysInMonth - 1); // 0 to 1

    // Linear interpolation between starting and ending weights
    const currentSeasonWeight = 0.7 - progress * 0.3; // 0.7 to 0.4
    const nextSeasonWeight = 0.3 + progress * 0.3; // 0.3 to 0.6

    return {
      primary: { season: currentSeason, weight: currentSeasonWeight },
      secondary: { season: nextSeason, weight: nextSeasonWeight },
    };
  }

  // If not in transition period, return just the current season
  return {
    primary: { season: currentSeason, weight: 1 },
  };
};

const getCurrentSeason = (): string => {
  const { primary } = getSeasonWeights();
  return primary.season;
};

export const OutfitBuilder: React.FC<OutfitBuilderProps> = ({
  items,
  onItemUpdate,
}): JSX.Element => {
  const { isMobile, isTablet } = useResponsive();
  const [state, setState] = useState<State>({
    selectedItem: null,
    compatibleItems: [],
    selectedCategory: "all",
    selectedOutfit: [],
    selectedIds: new Set<string>(),
  });

  // State for item compatibility scores
  const [itemCompatibilityScores, setItemCompatibilityScores] = React.useState<
    Map<string, number>
  >(new Map());

  // State for individual score breakdowns (color, occasion, season)
  const [itemScoreBreakdowns, setItemScoreBreakdowns] = React.useState<
    Map<
      string,
      { colorScore: number; occasionScore: number; seasonScore: number }
    >
  >(new Map());

  const {
    selectedItem,
    compatibleItems,
    selectedCategory,
    selectedOutfit,
    selectedIds,
  } = state;

  // Use the weights directly instead of just the current season
  const seasonWeights = getSeasonWeights();
  const [currentSeason] = useState<string>(seasonWeights.primary.season);

  // Synchronous version for sorting - uses cached scores from state
  const getItemCompatibilityScoreSync = (item: ClothingItem): number => {
    if (selectedOutfit.length === 0) return 1.0;

    // Use cached score from state if available, otherwise return neutral score
    return itemCompatibilityScores.get(item._id) || 0.5;
  };

  const getOutfitRules = (
    currentOutfit: ClothingItem[]
  ): Record<ClothingType, number> => {
    // Maximum allowed items per type in an outfit
    const baseRules: Record<ClothingType, number> = {
      top: 1,
      bottom: 1,
      dress: 1,
      outerwear: 1,
      shoes: 1,
      accessory: 3, // Allow multiple accessories
    };

    // If we have a dress, we can't add tops or bottoms
    if (currentOutfit.some((item) => item.type === "dress")) {
      baseRules.top = 0;
      baseRules.bottom = 0;
    }

    // If we have a top or bottom, we can't add a dress
    if (
      currentOutfit.some(
        (item) => item.type === "top" || item.type === "bottom"
      )
    ) {
      baseRules.dress = 0;
    }

    return baseRules;
  };

  const getCurrentTypeCounts = (
    outfit: ClothingItem[]
  ): Record<ClothingType, number> => {
    const counts: Record<ClothingType, number> = {
      top: 0,
      bottom: 0,
      dress: 0,
      outerwear: 0,
      shoes: 0,
      accessory: 0,
    };

    outfit.forEach((item) => {
      counts[item.type]++;
    });

    return counts;
  };

  const canAddItemType = (
    type: ClothingType,
    currentOutfit: ClothingItem[]
  ): boolean => {
    const rules = getOutfitRules(currentOutfit);
    const counts = getCurrentTypeCounts(currentOutfit);
    return counts[type] < rules[type];
  };

  // Get available items when no item is selected
  const getAvailableItems = (itemsToFilter: ClothingItem[]): ClothingItem[] => {
    if (!selectedOutfit.length) return itemsToFilter;

    return itemsToFilter.filter((item) => {
      // Check if we can add more items of this type based on outfit rules
      return canAddItemType(item.type, selectedOutfit);
    });
  };

  // Get compatible items when an item is selected
  useEffect(() => {
    const fetchCompatibleItems = async () => {
      if (selectedItem) {
        try {
          // Filter compatible items based on type, color, and style
          const compatible: ClothingItem[] = items
            .filter((item: ClothingItem) => {
              // Don't show the selected item
              if (item._id === selectedItem._id) return false;

              // Check if we can add more items of this type
              if (!canAddItemType(item.type, selectedOutfit)) return false;

              // Check if the item types are complementary
              const isComplementary = isComplementaryType(
                selectedItem.type,
                item.type
              );
              if (!isComplementary) return false;

              // Check if the items share at least one season
              const hasMatchingSeason = selectedItem.seasons.some(
                (season: string) => item.seasons.includes(season)
              );
              if (!hasMatchingSeason) return false;

              // Check if the occasions match or one is casual
              const matchingOccasion =
                (item.occasions || []).some((occ) =>
                  (selectedItem.occasions || []).includes(occ)
                ) ||
                (item.occasions || []).includes("casual") ||
                (selectedItem.occasions || []).includes("casual");

              return matchingOccasion;
            })
            .sort((a, b) => {
              // Sort by overall compatibility score
              const scoreA = getItemCompatibilityScoreSync(a);
              const scoreB = getItemCompatibilityScoreSync(b);
              return scoreB - scoreA; // Higher scores first
            });

          setState((prev) => ({ ...prev, compatibleItems: compatible }));
        } catch (error) {
          console.error("Error fetching compatible items:", error);
          setState((prev) => ({ ...prev, compatibleItems: [] }));
        }
      } else {
        setState((prev) => ({ ...prev, compatibleItems: [] }));
      }
    };

    fetchCompatibleItems();
  }, [selectedItem, items, selectedOutfit]);

  const isComplementaryType = (
    type1: ClothingType,
    type2: ClothingType
  ): boolean => {
    const complementaryPairs: Record<ClothingType, ClothingType[]> = {
      top: ["bottom", "shoes", "accessory", "outerwear"] as ClothingType[],
      bottom: ["top", "shoes", "accessory", "outerwear"] as ClothingType[],
      dress: ["shoes", "accessory", "outerwear"] as ClothingType[],
      shoes: [
        "top",
        "bottom",
        "dress",
        "accessory",
        "outerwear",
      ] as ClothingType[],
      accessory: [
        "top",
        "bottom",
        "dress",
        "shoes",
        "outerwear",
      ] as ClothingType[],
      outerwear: [
        "top",
        "bottom",
        "dress",
        "accessory",
        "shoes",
      ] as ClothingType[],
    };

    return complementaryPairs[type1]?.includes(type2) || false;
  };

  const handleItemClick = (item: ClothingItem) => {
    if (selectedIds.has(item._id)) {
      // If item is already selected, remove it and its compatible items
      setState((prev) => ({
        ...prev,
        selectedItem: null,
        selectedOutfit: prev.selectedOutfit.filter((i) => i._id !== item._id),
        selectedIds: new Set(
          Array.from(prev.selectedIds).filter((id) => id !== item._id)
        ),
      }));
    } else {
      setState((prev) => ({
        ...prev,
        selectedItem: item,
        selectedOutfit: [item],
        selectedIds: new Set<string>([item._id]),
      }));
    }
  };

  const handleCompatibleItemClick = (item: ClothingItem) => {
    setState((prev) => {
      const newSelectedIds = new Set(prev.selectedIds);
      const newSelectedOutfit = [...prev.selectedOutfit];

      if (newSelectedIds.has(item._id)) {
        // If item is already selected, remove it
        newSelectedIds.delete(item._id);
        const index = newSelectedOutfit.findIndex((i) => i._id === item._id);
        if (index !== -1) {
          newSelectedOutfit.splice(index, 1);
        }
      } else {
        // Add the new item
        newSelectedIds.add(item._id);
        newSelectedOutfit.push(item);
      }

      return {
        ...prev,
        selectedOutfit: newSelectedOutfit,
        selectedIds: newSelectedIds,
      };
    });
  };

  const handleOutfitItemClick = (item: ClothingItem) => {
    setState((prev) => {
      const newSelectedIds = new Set(prev.selectedIds);
      const newSelectedOutfit = prev.selectedOutfit.filter(
        (i) => i._id !== item._id
      );
      newSelectedIds.delete(item._id);

      // If we removed the main selected item, clear the selection
      const newState = {
        ...prev,
        selectedOutfit: newSelectedOutfit,
        selectedIds: newSelectedIds,
      };

      if (item._id === prev.selectedItem?._id) {
        newState.selectedItem = null;
      }

      return newState;
    });
  };

  const handleCategoryChange = (value: ClothingType | "all") => {
    setState((prev: State) => ({ ...prev, selectedCategory: value }));
  };

  const categories: (ClothingType | "all")[] = [
    "all",
    "top",
    "bottom",
    "dress",
    "shoes",
    "accessory",
    "outerwear",
  ];

  const getOccasionSymmetryScore = (outfit: ClothingItem[]): number => {
    if (outfit.length === 0) return 0;

    // For each item, calculate how many other items it matches with
    let totalMatchScore = 0;
    let totalPossibleMatches = 0;

    for (let i = 0; i < outfit.length; i++) {
      const item = outfit[i];
      const itemOccasions = new Set(item.occasions || []);

      for (let j = i + 1; j < outfit.length; j++) {
        const otherItem = outfit[j];
        const otherOccasions = new Set(otherItem.occasions || []);

        // Find shared occasions
        const sharedOccasions = Array.from(itemOccasions).filter((x) =>
          otherOccasions.has(x)
        );

        // Calculate match score for this pair
        let pairScore = 0;

        if (sharedOccasions.length > 0) {
          // If they share any non-casual occasions, perfect match
          if (sharedOccasions.some((occ) => occ !== "casual")) {
            pairScore = 1.0;
          }
          // If they only share "casual", good but not perfect match
          else if (sharedOccasions.includes("casual")) {
            pairScore = 0.8;
          }
        }
        // If no shared occasions but one is casual, small compatibility
        else if (itemOccasions.has("casual") || otherOccasions.has("casual")) {
          pairScore = 0.3;
        }
        // If mixing business/formal with sport, very low compatibility
        else if (
          ((itemOccasions.has("business") || itemOccasions.has("formal")) &&
            otherOccasions.has("sport")) ||
          ((otherOccasions.has("business") || otherOccasions.has("formal")) &&
            itemOccasions.has("sport"))
        ) {
          pairScore = 0.1;
        }

        totalMatchScore += pairScore;
        totalPossibleMatches += 1;
      }
    }

    // If there's only one item, return perfect score
    if (totalPossibleMatches === 0) return 1.0;

    // Calculate final score
    const score = totalMatchScore / totalPossibleMatches;

    // Cap the final score at 1.0
    return Math.min(score, 1.0);
  };

  const getColorCompatibility = (item: ClothingItem): number => {
    if (!selectedOutfit.length || !item.dominantColors?.length) return 1;

    // Get all dominant colors from currently selected items
    const selectedColors = selectedOutfit.flatMap(
      (selected) => selected.dominantColors || []
    );

    // Get the types for proper monochrome detection
    const selectedTypes = selectedOutfit.map((selected) => selected.type);
    // Helper function to convert hex/rgb string to RGB object
    const parseColorToRGB = (
      color: string | any
    ): { r: number; g: number; b: number } => {
      try {
        if (typeof color === "object" && color.r !== undefined) {
          return color; // Already an RGB object
        }

        if (typeof color === "string") {
          // Handle hex colors like "#FFFFFF" or "#FFF"
          if (color.startsWith("#")) {
            const hex = color.replace("#", "");
            if (hex.length === 3) {
              // Handle short hex like "#FFF"
              const r = parseInt(hex[0] + hex[0], 16);
              const g = parseInt(hex[1] + hex[1], 16);
              const b = parseInt(hex[2] + hex[2], 16);

              // Validate parsed values
              if (isNaN(r) || isNaN(g) || isNaN(b)) {
                console.warn(
                  `Invalid 3-char hex values parsed from: ${color}`,
                  { r, g, b }
                );
                return { r: 128, g: 128, b: 128 }; // Fallback to gray
              }

              return { r, g, b };
            } else if (hex.length === 6) {
              // Handle full hex like "#FFFFFF"
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);

              // Validate parsed values
              if (isNaN(r) || isNaN(g) || isNaN(b)) {
                console.warn(`Invalid hex values parsed from: ${color}`, {
                  r,
                  g,
                  b,
                });
                return { r: 128, g: 128, b: 128 }; // Fallback to gray
              }

              return { r, g, b };
            }
          }

          // Handle rgb() strings like "rgb(255, 255, 255)"
          if (color.startsWith("rgb(")) {
            const values = color.match(/\d+/g);
            if (values && values.length >= 3) {
              const r = parseInt(values[0]);
              const g = parseInt(values[1]);
              const b = parseInt(values[2]);

              // Validate parsed values
              if (isNaN(r) || isNaN(g) || isNaN(b)) {
                console.warn(`Invalid RGB values parsed from: ${color}`, {
                  r,
                  g,
                  b,
                });
                return { r: 128, g: 128, b: 128 }; // Fallback to gray
              }

              return { r, g, b };
            }
          }
        }
      } catch (error) {
        console.warn("Error parsing color:", color, error);
      }

      // Fallback to gray
      return { r: 128, g: 128, b: 128 };
    };

    // Use hybrid color compatibility (ML + rules)
    const color1 = parseColorToRGB(selectedColors[0]);
    const color2 = parseColorToRGB(item.dominantColors?.[0]);

    // Debug: Log the parsed colors (reduced logging for performance)

    try {
      const result = hybridColorService.getHybridCompatibilityScore(
        color1,
        color2,
        {
          garmentTypes: [
            ...selectedOutfit.map((selected) => selected.type),
            item.type,
          ],
        }
      );

      return result.hybridScore;
    } catch (error) {
      console.error(
        `❌ Error in color compatibility calculation for ${item.name}:`,
        error
      );
      return 0.5; // Return neutral score on error
    }
  };

  // Add season compatibility scoring
  const getSeasonCompatibilityScore = (item: ClothingItem): number => {
    if (!item.seasons || item.seasons.length === 0) return 0.5; // Default score if no seasons specified

    // Convert everything to lowercase for comparison
    const itemSeasons = item.seasons.map((s) => s.toLowerCase());

    let totalScore = 0;
    let maxPossibleScore = 0;

    // Calculate score for primary season
    if (itemSeasons.includes(seasonWeights.primary.season)) {
      totalScore += 1.0 * seasonWeights.primary.weight;
    }
    maxPossibleScore += seasonWeights.primary.weight;

    // Calculate score for secondary season if in transition period
    if (seasonWeights.secondary) {
      if (itemSeasons.includes(seasonWeights.secondary.season)) {
        totalScore += 1.0 * seasonWeights.secondary.weight;
      }
      maxPossibleScore += seasonWeights.secondary.weight;
    }

    // If the item doesn't match either season, calculate distance-based score
    if (totalScore === 0) {
      const seasonOrder = ["spring", "summer", "autumn", "winter"];
      const primaryIndex = seasonOrder.indexOf(seasonWeights.primary.season);

      let minDistance = seasonOrder.length;
      for (const itemSeason of itemSeasons) {
        const itemSeasonIndex = seasonOrder.indexOf(itemSeason);
        let distance = Math.abs(primaryIndex - itemSeasonIndex);
        // Account for circular nature of seasons (winter->spring)
        distance = Math.min(distance, seasonOrder.length - distance);
        minDistance = Math.min(minDistance, distance);
      }

      // Convert distance to score (2->0.3, 1->0.6, 0->1.0)
      const distanceScore = 1 - minDistance * 0.35;
      return distanceScore;
    }

    return totalScore / maxPossibleScore;
  };

  // Filter items by category and availability, then sort by compatibility
  const filteredItems: ClothingItem[] = getAvailableItems(
    selectedCategory === "all"
      ? items
      : items.filter((item: ClothingItem) => item.type === selectedCategory)
  ).sort((a, b) => {
    // Sort by compatibility score if we have selected items
    if (selectedOutfit.length > 0) {
      return (
        getItemCompatibilityScoreSync(b) - getItemCompatibilityScoreSync(a)
      );
    }
    return 0; // Keep original order if no items selected
  });

  const getCompatibilityColor = (score: number): string => {
    if (score >= 0.8) return "success.main";
    if (score >= 0.5) return "warning.main";
    return "error.main";
  };

  const renderItemCard = (
    item: ClothingItem,
    isCompatible: boolean = false
  ) => {
    // Get cached scores from state
    const scoreBreakdown = itemScoreBreakdowns.get(item._id);

    // For preview scores, use cached values if available, otherwise show loading state
    const colorScore =
      scoreBreakdown?.colorScore ?? (selectedOutfit.length ? null : 1.0);
    const occasionScore =
      scoreBreakdown?.occasionScore ?? (selectedOutfit.length ? null : 1.0);
    const seasonScore =
      scoreBreakdown?.seasonScore ?? getSeasonCompatibilityScore(item);

    // Get overall compatibility score from state
    const overallScore =
      itemCompatibilityScores.get(item._id) ??
      (selectedOutfit.length ? null : 1.0);

    const backgroundColor = selectedIds.has(item._id)
      ? "#AABBA5" // Sage Green for selected items
      : "#FFFCF7"; // Ivory White for regular items

    return (
      <Card
        key={item._id}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: backgroundColor,
          border: "1px solid #C49A88",
          borderRadius: 2,
          opacity: overallScore === 0 ? 0.5 : 1,
          "&:hover": {
            boxShadow: "0 4px 12px rgba(26, 26, 26, 0.1)",
          },
        }}
      >
        <CardActionArea
          onClick={() =>
            isCompatible
              ? handleCompatibleItemClick(item)
              : handleItemClick(item)
          }
          disabled={overallScore === 0}
        >
          <CardMedia
            component="img"
            height="200"
            image={item.imageUrl}
            alt={item.name}
            sx={{ objectFit: "contain", bgcolor: "#F4EDE4" }}
          />
          <CardContent>
            <Typography variant="body1" gutterBottom sx={{ color: "#1A1A1A" }}>
              {item.name}
            </Typography>
            {selectedOutfit.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Chip
                  label={
                    colorScore !== null
                      ? `Color: ${Math.round(colorScore * 100)}%`
                      : "Color: Calculating..."
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      colorScore !== null
                        ? colorScore > 0.7
                          ? "#AABBA5"
                          : "#C49A88"
                        : "#6B7B8C",
                    color: "#1A1A1A",
                  }}
                />
                <Chip
                  label={
                    occasionScore !== null
                      ? `Occasion: ${Math.round(occasionScore * 100)}%`
                      : "Occasion: Calculating..."
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      occasionScore !== null
                        ? occasionScore > 0.7
                          ? "#AABBA5"
                          : "#C49A88"
                        : "#6B7B8C",
                    color: "#1A1A1A",
                  }}
                />
                <Chip
                  label={`Season: ${Math.round(seasonScore * 100)}%`}
                  size="small"
                  sx={{
                    backgroundColor: seasonScore > 0.7 ? "#AABBA5" : "#C49A88",
                    color: "#1A1A1A",
                  }}
                />
                <Chip
                  label={
                    overallScore !== null
                      ? `Overall: ${Math.round(overallScore * 100)}%`
                      : "Overall: Calculating..."
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      overallScore !== null
                        ? overallScore > 0.7
                          ? "#AABBA5"
                          : "#C49A88"
                        : "#6B7B8C",
                    color: "#1A1A1A",
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1,
              }}
            >
              {item.occasions?.map((occasion) => (
                <Chip
                  key={occasion}
                  label={occasion}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: "#C49A88",
                    color: "#6B7B8C",
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  // Group items by type
  const getItemsByType = (
    items: ClothingItem[]
  ): Record<ClothingType, ClothingItem[]> => {
    const grouped: Record<ClothingType, ClothingItem[]> = {
      top: [],
      bottom: [],
      dress: [],
      outerwear: [],
      shoes: [],
      accessory: [],
    };

    items.forEach((item) => {
      grouped[item.type].push(item);
    });

    // If we have selected items, sort each group by overall compatibility
    if (selectedOutfit.length > 0) {
      Object.keys(grouped).forEach((type) => {
        grouped[type as ClothingType].sort((a, b) => {
          const scoreA = getItemCompatibilityScoreSync(a);
          const scoreB = getItemCompatibilityScoreSync(b);
          return scoreB - scoreA; // Higher scores first
        });
      });
    }

    return grouped;
  };

  const getOutfitScore = async (
    outfit: ClothingItem[]
  ): Promise<{ score: number; explanations: string[] }> => {
    const explanations: string[] = [];
    const colorWarnings = new Set<string>();
    const colorPositives = new Set<string>();
    const notes: string[] = [];

    // Check for monochromatic outfit
    const allColors = outfit.flatMap((item) => item.dominantColors || []);
    const { isMonochrome, isAllBlack, isAllWhite } = isMonochromaticOutfit(
      allColors,
      outfit.map((item) => ({ type: item.type }))
    );

    // Calculate color score
    let totalColorScore = 0;
    let colorPairs = 0;

    for (let i = 0; i < outfit.length; i++) {
      for (let j = i + 1; j < outfit.length; j++) {
        // Helper function to convert hex/rgb string to RGB object
        const parseColorToRGB = (
          color: string | any
        ): { r: number; g: number; b: number } => {
          if (typeof color === "object" && color.r !== undefined) {
            return color; // Already an RGB object
          }

          if (typeof color === "string") {
            // Handle hex colors like "#FFFFFF" or "#FFF"
            if (color.startsWith("#")) {
              const hex = color.replace("#", "");
              const r = parseInt(hex.substr(0, 2), 16);
              const g = parseInt(hex.substr(2, 2), 16);
              const b = parseInt(hex.substr(4, 2), 16);
              return { r, g, b };
            }

            // Handle rgb() strings like "rgb(255, 255, 255)"
            if (color.startsWith("rgb(")) {
              const values = color.match(/\d+/g);
              if (values && values.length >= 3) {
                return {
                  r: parseInt(values[0]),
                  g: parseInt(values[1]),
                  b: parseInt(values[2]),
                };
              }
            }
          }

          // Fallback to gray
          return { r: 128, g: 128, b: 128 };
        };

        const color1: { r: number; g: number; b: number } = parseColorToRGB(
          outfit[i].dominantColors?.[0]
        );
        const color2: { r: number; g: number; b: number } = parseColorToRGB(
          outfit[j].dominantColors?.[0]
        );

        const result = await hybridColorService.getHybridCompatibilityScore(
          color1,
          color2,
          {
            garmentTypes: [outfit[i].type, outfit[j].type],
          }
        );
        totalColorScore += result.hybridScore;
        colorPairs++;

        // Only collect feedback if not an all-white outfit
        if (!isAllWhite) {
          (result as any).explanation.forEach((exp: string) => {
            if (exp.includes("⚠️")) {
              colorWarnings.add(exp);
            } else if (exp.includes("✨") || exp.includes("👍")) {
              // Only add positive feedback if the outfit is not monochromatic
              if (!isMonochrome || exp.includes("neutral")) {
                colorPositives.add(exp);
              }
            }
          });
        }
      }
    }

    const colorScore = colorPairs > 0 ? totalColorScore / colorPairs : 1;

    // Get occasion and season scores
    const occasionScore = getOccasionSymmetryScore(outfit);
    let totalSeasonScore = 0;
    outfit.forEach((item) => {
      totalSeasonScore += getSeasonCompatibilityScore(item);
    });
    const seasonScore = totalSeasonScore / outfit.length;

    // Add the three main scores
    explanations.push(`🎨 Color Harmony: ${Math.round(colorScore * 100)}%`);
    explanations.push(`👔 Occasion Match: ${Math.round(occasionScore * 100)}%`);
    explanations.push(`🌤️ Season Fit: ${Math.round(seasonScore * 100)}%`);

    // Add monochromatic notes if any (deduplicate first)
    const uniqueNotes = Array.from(new Set(notes));
    if (uniqueNotes.length > 0) {
      explanations.push(""); // Add a blank line for spacing
      explanations.push(...uniqueNotes);
    }

    // Add any other warnings or positive feedback
    const otherFeedback = [
      ...Array.from(colorWarnings),
      ...Array.from(colorPositives),
    ];

    if (otherFeedback.length > 0) {
      explanations.push(""); // Add a blank line for spacing
      explanations.push(...otherFeedback);
    }

    // Calculate final score with heavier weight on color harmony for monochromatic outfits
    const finalScore =
      isMonochrome && !isAllBlack
        ? colorScore * 0.6 + occasionScore * 0.25 + seasonScore * 0.15 // More weight on color for monochromatic
        : colorScore * 0.4 + occasionScore * 0.35 + seasonScore * 0.25; // Normal weights

    return {
      score: finalScore,
      explanations,
    };
  };

  // State for outfit scoring
  const [outfitScore, setOutfitScore] = React.useState<number>(0.5);
  const [outfitExplanations, setOutfitExplanations] = React.useState<string[]>(
    []
  );
  const [isScoreLoading, setIsScoreLoading] = React.useState<boolean>(false);
  const [isCalculatingCompatibility, setIsCalculatingCompatibility] =
    React.useState<boolean>(false);

  // Ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = React.useRef(true);

  // Async version for actual calculations
  const getItemCompatibilityScore = async (
    item: ClothingItem
  ): Promise<number> => {
    if (selectedOutfit.length === 0) {
      return 1.0;
    }

    try {
      // Get real color compatibility score using the proper function
      const colorScore = getColorCompatibility(item);

      const occasionScore = getOccasionSymmetryScore([...selectedOutfit, item]);
      const seasonScore = getSeasonCompatibilityScore(item);

      // Weight the scores - adjust weights to emphasize color compatibility
      const finalScore =
        colorScore * 0.5 + occasionScore * 0.3 + seasonScore * 0.2;

      return finalScore;
    } catch (error) {
      console.error(
        `❌ Error in getItemCompatibilityScore for ${item.name}:`,
        error
      );
      return 0.5;
    }
  };

  // Update outfit score when selectedOutfit changes
  React.useEffect(() => {
    if (selectedOutfit.length === 0) return;

    setIsScoreLoading(true);
    getOutfitScore(selectedOutfit)
      .then((result) => {
        setOutfitScore(result.score);
        setOutfitExplanations(result.explanations);
      })
      .catch((error) => {
        console.error("Error calculating outfit score:", error);
        setOutfitScore(0.5);
        setOutfitExplanations(["Error calculating score"]);
      })
      .finally(() => {
        setIsScoreLoading(false);
      });
  }, [selectedOutfit]);

  // Update item compatibility scores when selectedOutfit changes
  React.useEffect(() => {
    if (selectedOutfit.length === 0) {
      setItemCompatibilityScores(new Map());
      setItemScoreBreakdowns(new Map());
      setIsCalculatingCompatibility(false);
      return;
    }

    // Debounce the calculations to avoid rapid recalculations
    const timeoutId = setTimeout(async () => {
      setIsCalculatingCompatibility(true);

      // Add a safety timeout to prevent hanging
      const safetyTimeout = setTimeout(() => {
        console.warn("⚠️ Compatibility calculation timed out, resetting state");
        if (isMountedRef.current) {
          setIsCalculatingCompatibility(false);
        }
      }, 10000); // 10 second timeout

      try {
        const newScores = new Map<string, number>();
        const newBreakdowns = new Map<
          string,
          { colorScore: number; occasionScore: number; seasonScore: number }
        >();

        // Calculate scores for all items, but prioritize visible items first
        const itemsToCalculate = items;

        // Calculate compatibility scores in batches to avoid overwhelming the system
        const batchSize = 3; // Reduced batch size for better performance
        const totalBatches = Math.ceil(itemsToCalculate.length / batchSize);

        for (let i = 0; i < itemsToCalculate.length; i += batchSize) {
          const batch = itemsToCalculate.slice(i, i + batchSize);
          const currentBatch = Math.floor(i / batchSize) + 1;

          const batchPromises = batch.map(async (item) => {
            const startTime = Date.now();

            try {
              // Add timeout protection for individual item calculations
              const scorePromise = getItemCompatibilityScore(item);
              const timeoutPromise = new Promise<number>((_, reject) =>
                setTimeout(
                  () =>
                    reject(new Error(`Calculation timeout for ${item.name}`)),
                  8000
                )
              );

              const score = await Promise.race([scorePromise, timeoutPromise]);
              const scoreTime = Date.now() - startTime;
              newScores.set(item._id, score);

              // Calculate individual score breakdowns
              const colorScore = getColorCompatibility(item);
              const occasionScore = getOccasionSymmetryScore([
                ...selectedOutfit,
                item,
              ]);
              const seasonScore = getSeasonCompatibilityScore(item);

              newBreakdowns.set(item._id, {
                colorScore,
                occasionScore,
                seasonScore,
              });

              const totalTime = Date.now() - startTime;
            } catch (error) {
              const errorTime = Date.now() - startTime;
              console.error(
                `❌ Error calculating score for item: ${item.name} after ${errorTime}ms`,
                error
              );
              // Set default scores for failed calculations
              newScores.set(item._id, 0.5);
              newBreakdowns.set(item._id, {
                colorScore: 0.5,
                occasionScore: 0.5,
                seasonScore: 0.5,
              });
            }
          });

          await Promise.all(batchPromises);

          // Update state after each batch to show progress (only if component is still mounted)
          if (isMountedRef.current) {
            setItemCompatibilityScores(new Map(newScores));
            setItemScoreBreakdowns(new Map(newBreakdowns));
          }

          // Add a small delay between batches to prevent overwhelming the system
          if (currentBatch < totalBatches) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      } catch (error) {
        console.error("Error in compatibility calculation:", error);
      } finally {
        // Clear the safety timeout
        clearTimeout(safetyTimeout);
        // Always reset the calculating state, regardless of success or failure (only if component is still mounted)
        if (isMountedRef.current) {
          setIsCalculatingCompatibility(false);
        }
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(timeoutId);
      // Reset calculating state when effect is cleaned up (only if component is still mounted)
      if (isMountedRef.current) {
        setIsCalculatingCompatibility(false);
      }
    };
  }, [selectedOutfit, items]);

  // Cleanup effect to track component mount status
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safety effect to reset calculating state if it gets stuck
  React.useEffect(() => {
    if (!isCalculatingCompatibility) return;

    const safetyTimeout = setTimeout(() => {
      console.warn("⚠️ Safety timeout: Resetting stuck calculating state");
      if (isMountedRef.current) {
        setIsCalculatingCompatibility(false);
      }
    }, 15000); // 15 second safety timeout

    return () => clearTimeout(safetyTimeout);
  }, [isCalculatingCompatibility]);

  // Initialize color harmony service on component mount
  React.useEffect(() => {
    const initializeColorHarmony = async () => {
      try {
        await hybridColorService.getHybridCompatibilityScore(
          { r: 128, g: 128, b: 128 },
          { r: 255, g: 255, b: 255 },
          { garmentTypes: ["top", "bottom"] }
        );
      } catch (error) {
        console.error("❌ Color harmony service initialization failed:", error);
      }
    };

    initializeColorHarmony();
  }, []);

  // Update the outfit score display
  const renderOutfitScore = (outfit: ClothingItem[]): JSX.Element | null => {
    if (outfit.length === 0) return null;
    const scoreColor =
      outfitScore >= 0.8
        ? "#AABBA5"
        : outfitScore >= 0.5
        ? "#C49A88"
        : "#FF6B6B";

    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="body2" sx={{ color: "#6B7B8C" }}>
          Outfit Score
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <CircularProgress
            variant={isScoreLoading ? "indeterminate" : "determinate"}
            value={isScoreLoading ? 0 : outfitScore * 100}
            size={40}
            sx={{
              color: scoreColor,
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Stack spacing={1} width="100%">
            <Typography
              variant="body2"
              sx={{
                color: "#1A1A1A",
                fontWeight: "medium",
              }}
            >
              Overall:{" "}
              {isScoreLoading
                ? "Calculating..."
                : `${Math.round(outfitScore * 100)}%`}
            </Typography>
            <Box
              sx={{
                bgcolor: "#F4EDE4",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid #C49A88",
              }}
            >
              {isScoreLoading ? (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    color: "#6B7B8C",
                    fontStyle: "italic",
                  }}
                >
                  Analyzing outfit compatibility...
                </Typography>
              ) : (
                outfitExplanations.map((explanation, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    display="block"
                    sx={{
                      color: "#6B7B8C",
                      mb: explanation === "" ? 1 : 0.5,
                      fontWeight:
                        explanation.includes("Color Harmony") ||
                        explanation.includes("Occasion Match") ||
                        explanation.includes("Season Fit")
                          ? "medium"
                          : "normal",
                      fontStyle: explanation.startsWith("•")
                        ? "italic"
                        : "normal",
                    }}
                  >
                    {explanation}
                  </Typography>
                ))
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 3,
        backgroundColor: "#F4EDE4",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: isMobile ? 2 : 2,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ color: "#1A1A1A" }}>
          Outfit Builder
        </Typography>
      </Box>

      <Grid container spacing={isMobile ? 1 : 3}>
        {/* Left side: Outfit Preview */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              minHeight: "calc(100vh - 100px)",
              maxHeight: "calc(100vh - 40px)",
              position: "sticky",
              top: 24,
              overflow: "auto",
              backgroundColor: "#FFFCF7",
              border: "1px solid #C49A88",
              borderRadius: 2,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#F4EDE4",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#C49A88",
                borderRadius: "4px",
                "&:hover": {
                  background: "#B08570",
                },
              },
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: "#1A1A1A" }}>
              Current Outfit
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#6B7B8C",
                fontSize: "0.7rem",
                fontStyle: "italic",
                mt: 1,
                display: "block",
              }}
            >
              Color harmony is an ongoing work in progress. Please feel free to
              submit feedback on the homepage or email screenshots to
              wardrobestylistai@gmail.com to help improve our recommendation
              system.
            </Typography>
            {renderOutfitScore(selectedOutfit)}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: "calc(100% - 40px)",
              }}
            >
              {/* Group items by type in the correct order */}
              {(
                [
                  "top",
                  "dress",
                  "bottom",
                  "outerwear",
                  "shoes",
                  "accessory",
                ] as ClothingType[]
              ).map((type) => {
                const itemsOfType = selectedOutfit.filter(
                  (item) => item.type === type
                );
                if (itemsOfType.length === 0) return null;

                return itemsOfType.map((item) => (
                  <Card
                    key={item._id}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "#FFFCF7",
                      border: "1px solid #C49A88",
                      borderRadius: 2,
                      "&:hover": {
                        opacity: 0.8,
                        transform: "scale(0.98)",
                        boxShadow: "0 4px 12px rgba(26, 26, 26, 0.1)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => handleOutfitItemClick(item)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.imageUrl}
                      alt={item.name}
                      sx={{ objectFit: "contain", bgcolor: "#F4EDE4" }}
                    />
                    <CardContent>
                      <Typography variant="body2" sx={{ color: "#1A1A1A" }}>
                        {item.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Chip
                          label={item.type}
                          size="small"
                          sx={{
                            backgroundColor: "#1A1A1A",
                            color: "#FFFCF7",
                          }}
                        />
                        {(item.occasions || []).map((occasion: string) => (
                          <Chip
                            key={occasion}
                            label={occasion}
                            size="small"
                            sx={{
                              backgroundColor: "#AABBA5",
                              color: "#1A1A1A",
                            }}
                          />
                        ))}
                      </Box>
                      {item.dominantColors &&
                        item.dominantColors.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              {item.dominantColors.map((color, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    bgcolor: color,
                                    border: "1px solid #C49A88",
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                    </CardContent>
                  </Card>
                ));
              })}
              {selectedOutfit.length === 0 && (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#F4EDE4",
                    borderRadius: 1,
                    p: 3,
                    border: "1px solid #C49A88",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: "#6B7B8C" }}
                    align="center"
                  >
                    Select items to build your outfit
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right side: Item Selection */}
        <Grid item xs={12} md={8}>
          <Box>
            {!selectedItem ? (
              // Available Items View
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Typography variant="h6" sx={{ color: "#1A1A1A" }}>
                    Available Items
                  </Typography>
                  {isCalculatingCompatibility && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" sx={{ color: "#6B7B8C" }}>
                        Calculating compatibility...
                      </Typography>
                    </Box>
                  )}
                </Box>
                {Object.entries(getItemsByType(filteredItems)).map(
                  ([type, items]) => {
                    if (items.length === 0) return null;

                    return (
                      <Box key={type} sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 2,
                            textTransform: "capitalize",
                            fontWeight: 500,
                            color: "#1A1A1A",
                          }}
                        >
                          {type}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            overflowX: "auto",
                            pb: 2,
                            "&::-webkit-scrollbar": {
                              height: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "#F4EDE4",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: "#C49A88",
                              borderRadius: "4px",
                              "&:hover": {
                                background: "#B08570",
                              },
                            },
                          }}
                        >
                          {items.map((item) => (
                            <Box
                              key={item._id}
                              sx={{
                                minWidth: 250,
                                maxWidth: 250,
                                flexShrink: 0,
                              }}
                            >
                              {renderItemCard(item)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  }
                )}
              </Box>
            ) : (
              // Compatible Items View
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Typography variant="h6" sx={{ color: "#1A1A1A" }}>
                    Compatible with {selectedItem.name}
                  </Typography>
                  <Chip
                    label="Clear Selection"
                    size="small"
                    onClick={() => {
                      setState((prev) => ({
                        ...prev,
                        selectedItem: null,
                        selectedOutfit: [],
                        selectedIds: new Set<string>(),
                      }));
                    }}
                    sx={{
                      ml: 2,
                      backgroundColor: "#C49A88",
                      color: "#1A1A1A",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#B08570",
                      },
                    }}
                  />
                </Box>
                {Object.entries(getItemsByType(compatibleItems)).map(
                  ([type, items]) => {
                    if (items.length === 0) return null;

                    return (
                      <Box key={type} sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 2,
                            textTransform: "capitalize",
                            fontWeight: 500,
                            color: "#1A1A1A",
                          }}
                        >
                          {type}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            overflowX: "auto",
                            pb: 2,
                            "&::-webkit-scrollbar": {
                              height: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "#F4EDE4",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: "#C49A88",
                              borderRadius: "4px",
                              "&:hover": {
                                background: "#B08570",
                              },
                            },
                          }}
                        >
                          {items.map((item) => (
                            <Box
                              key={item._id}
                              sx={{
                                minWidth: 250,
                                maxWidth: 250,
                                flexShrink: 0,
                              }}
                            >
                              {renderItemCard(item, true)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  }
                )}
                {compatibleItems.length === 0 && (
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: "#F4EDE4",
                      borderRadius: 1,
                      textAlign: "center",
                      border: "1px solid #C49A88",
                    }}
                  >
                    <Typography sx={{ color: "#6B7B8C" }}>
                      No compatible items found for {selectedItem.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
